import {
  PartialUserSettings,
  UserSettings,
  supplyDefaultUsersettings,
} from '@certhon/user-settings';
import { useCallback, useMemo } from 'react';

import { useIsOnline } from '@/features/offline/useSyncStatus';
import { useCurrentUser } from '@/features/users/users.hooks';
import useAuthService from '@/hooks/use-auth-service';

/**
 * Server-side user settings — the `cloud_usr.settings` jsonb column, carried
 * on the session user and written through `PATCH /me`.
 *
 * Unlike `useUserPreference` (localStorage, per-device) these follow the user
 * across devices. There is no separate query: AuthService already holds the
 * profile, persists it, and notifies subscribers, so settings render on the
 * first paint instead of popping in after a request.
 *
 * Nothing refreshes them from here either: AuthService pulls the profile on
 * init and gets a fresh one with every access-token renewal, so a change made
 * on another device lands on the next app start at the latest.
 */
export function useUserSettings(): UserSettings {
  const currentUser = useCurrentUser();

  return useMemo(
    () =>
      supplyDefaultUsersettings(
        (currentUser?.settings ?? {}) as PartialUserSettings,
      ),
    [currentUser?.settings],
  );
}

/**
 * Patch one or more user settings. Accepts a partial object or an updater that
 * receives the current (defaults-applied) settings.
 *
 * `canEdit` is false while offline: `PATCH /me` is not part of the offline
 * queue, so a write would be reverted as soon as it failed. Callers should
 * disable their controls rather than let the user make a change that vanishes.
 *
 * `updateSettings` resolves to whether the save succeeded and never rejects,
 * so fire-and-forget callers can ignore it while others can show feedback.
 */
export function useUpdateUserSettings(): {
  canEdit: boolean;
  updateSettings: (
    update:
      | PartialUserSettings
      | ((current: UserSettings) => PartialUserSettings),
  ) => Promise<boolean>;
} {
  const authService = useAuthService();
  const currentUser = useCurrentUser();
  const isOnline = useIsOnline();

  const updateSettings = useCallback(
    (
      update:
        | PartialUserSettings
        | ((current: UserSettings) => PartialUserSettings),
    ): Promise<boolean> => {
      const user = authService.getCurrentUser();
      if (!user) return Promise.resolve(false);

      // Read straight from the session rather than from a captured value: two
      // updates fired in the same tick must compose, and PATCH replaces the
      // whole settings object, so a stale base would drop the first change.
      const current = supplyDefaultUsersettings(
        (user.settings ?? {}) as PartialUserSettings,
      );
      const settings: UserSettings = {
        ...current,
        ...(typeof update === 'function' ? update(current) : update),
      };

      // AuthService applies it to the session immediately and rolls back if
      // the request fails.
      return authService.patchCurrentUser({ settings }).then(
        () => true,
        () => false, // Already logged and reverted by AuthService.
      );
    },
    [authService],
  );

  return { canEdit: isOnline && !!currentUser, updateSettings };
}
